import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { AlertService, IAlertType } from "./alert.service";
import { Observable, throwError } from "rxjs";
import { Order, Orders, OrdersList, JobPositions, WebserviceResponse, ProjectsComment } from "../interfaces";
import { catchError, retry } from "rxjs/operators";
import { restBaseUrl } from "../utils/rest-url";

@Injectable({
  providedIn: 'root'
})
export class OrdersStoreService {

  private readonly endpoint = restBaseUrl('/orders');

  constructor(
      private http: HttpClient,
      private alertDispatcher: AlertService
  ) {
  }

  private handleError(error: HttpErrorResponse){
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }
    // window.alert(errorMessage);

    this.alertDispatcher.show({
      message: errorMessage,
      clrAlertType: IAlertType.DANGER
    }, 5000);

    // return throwError(errorMessage); deprecated
    return throwError(() => new Error(errorMessage));
  }

  // Main CRUD Services

  public createOrder(prid: string, reqid:number, jobid: number): Observable<WebserviceResponse<boolean>> {
    return this.http.post<WebserviceResponse<boolean>>(
        `${this.endpoint}/add_orders?project_id=${prid}&request_id=${reqid}&job_id=${jobid}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateOrderTotal(projectID: string, orderID: number, total: number){
    return this.http.put(
        `${this.endpoint}/update_purchasing_price_selling_price?project_id=${projectID}&order_id=${orderID}&purchasing_price=${total}&selling_price=0`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public deleteOrder(projectID: string, orderID: number) {
    return this.http.delete(
        `${this.endpoint}/delete_orders?project_number=${projectID}&order_id=${orderID}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getOrders(projectId: string, orderId: number, jobId: number, status: string): Observable<WebserviceResponse<Orders[]>> {
    const options = new HttpParams({
      fromObject: {
        project_number: projectId,
        order_id: orderId,
        job_id: jobId,
        status: status
      }
    });
    return this.http.get<WebserviceResponse<Orders[]>>(this.endpoint + '/get_orders',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateOrder(o: Order, jobID: number, orderID: number) {
    return this.http.put(
        `${this.endpoint}/update_orders?order_id=${orderID}&job_id=${jobID}`,
        o,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateComment(oid: number, pid: string, comment: ProjectsComment) {
    return this.http.put(
        `${this.endpoint}/update_comment?order_id=${oid}&project_id=${pid}`,
        comment,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // Order Status

  public updateOrderStatus(projectID: string, orderID: string, status: string){
    return this.http.put(
        `${this.endpoint}/update_status?project_id=${projectID}&order_id=${orderID}&status=${status}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // Order Position Services

  public getOrdersPositions(projectID: string, orderID: number, jobID: number, positionID: string, partID: string): Observable<WebserviceResponse<OrdersList[]>> {
    const options = new HttpParams({
      fromObject: {
        project_number: projectID,
        order_id: orderID,
        job_id: jobID,
        position: positionID,
        part: partID
      }
    });
    return this.http.get<WebserviceResponse<OrdersList[]>>(this.endpoint + '/get_orders_position',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateOrdersPosition(order: OrdersList) {
    return this.http.put(
        `${this.endpoint}/update_orders_position`,
        order,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public deleteOrdersPosition(projectID: string, orderID: number, positionID: number, partID: number) {
    return this.http.delete(
        `${this.endpoint}/delete_orders_position?project_number=${projectID}&order_id=${orderID}&position=${positionID}&part=${partID}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getOrdersByJobId(projectID: string, jobID: number): Observable<WebserviceResponse<JobPositions[]>> {
    const options = new HttpParams({
      fromObject: {
        project_number: projectID,
        job_id: jobID,
      }
    });
    return this.http.get<WebserviceResponse<JobPositions[]>>(this.endpoint + '/get_orders_position_by_job_id',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public addSingleProjectPosition(projectID: string, orderID: number, positionID: number, partID: number) {
    return this.http.post(
        `${this.endpoint}/add_single_order_position?project_id=${projectID}&order_id=${orderID}&part=${partID}&position=${positionID}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }
}
